import * as Schema from "effect/Schema";
import { CommandId, EnvironmentId, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { EnvironmentCommandReceipt } from "./orchestration.ts";
import {
  PullRequestReviewVerdict,
  PullRequestRef,
  PullRequestMergeMethod,
  PullRequestReactionContent,
} from "./sourceControl.ts";

export const PullRequestEditInput = Schema.Struct({
  ...PullRequestRef.fields,
  environmentId: EnvironmentId,
  commandId: CommandId,
  expectedRevision: TrimmedNonEmptyString,
  resourceVersion: TrimmedNonEmptyString,
  title: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  body: Schema.String.check(Schema.isMaxLength(65_536)),
});
export type PullRequestEditInput = typeof PullRequestEditInput.Type;

export const PullRequestEditResult = Schema.Union([
  Schema.Struct({ status: Schema.Literal("settled"), receipt: EnvironmentCommandReceipt }),
  Schema.Struct({
    status: Schema.Literals(["uncertain", "unavailable", "idempotency-conflict"]),
    commandId: CommandId,
    message: TrimmedNonEmptyString,
  }),
]);
export type PullRequestEditResult = typeof PullRequestEditResult.Type;

export const PullRequestReviewComment = Schema.Struct({
  path: TrimmedNonEmptyString,
  line: Schema.Int.check(Schema.isGreaterThan(0)),
  side: Schema.Literals(["LEFT", "RIGHT"]),
  body: TrimmedNonEmptyString.check(Schema.isMaxLength(65000)),
});
export type PullRequestReviewComment = typeof PullRequestReviewComment.Type;
export const PullRequestReviewAction = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("submit-review"),
    codeVersion: TrimmedNonEmptyString,
    verdict: PullRequestReviewVerdict,
    body: Schema.String.check(Schema.isMaxLength(65000)),
    comments: Schema.Array(PullRequestReviewComment).check(Schema.isMaxLength(100)),
  }),
  Schema.Struct({
    kind: Schema.Literal("reply-review"),
    threadId: TrimmedNonEmptyString,
    body: TrimmedNonEmptyString.check(Schema.isMaxLength(65000)),
  }),
  Schema.Struct({
    kind: Schema.Literal("resolve-review"),
    threadId: TrimmedNonEmptyString,
    resolved: Schema.Boolean,
  }),
]);
export type PullRequestReviewAction = typeof PullRequestReviewAction.Type;

export const PullRequestMergeAction = Schema.Union([
  Schema.Struct({
    kind: Schema.Literals(["merge", "enable-auto-merge"]),
    method: PullRequestMergeMethod,
    expectedHeadOid: TrimmedNonEmptyString,
  }),
  Schema.Struct({ kind: Schema.Literal("disable-auto-merge") }),
]);
export type PullRequestMergeAction = typeof PullRequestMergeAction.Type;

export const PullRequestLifecycleAction = Schema.Union([
  PullRequestMergeAction,
  Schema.Struct({
    kind: Schema.Literal("lifecycle"),
    operation: Schema.Literals(["ready", "draft", "close", "reopen"]),
  }),
  Schema.Struct({
    kind: Schema.Literal("update-branch"),
    method: Schema.Literals(["merge", "rebase"]),
    expectedHeadOid: TrimmedNonEmptyString,
    expectedBaseOid: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    kind: Schema.Literal("request-reviewers"),
    users: Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(15)),
    teams: Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(15)),
  }),
  Schema.Struct({
    kind: Schema.Literal("set-labels"),
    /**
     * The labels the pull request should carry afterwards, not the ones to add. Stating the whole
     * set is what makes the write idempotent: repeating it cannot add a label twice, and two
     * readers racing cannot each append half of what they meant.
     */
    labels: Schema.Array(TrimmedNonEmptyString.check(Schema.isMaxLength(100))).check(
      Schema.isMaxLength(50),
    ),
  }),
]);
export type PullRequestLifecycleAction = typeof PullRequestLifecycleAction.Type;

export const PullRequestDiscussionAction = Schema.Union([
  PullRequestReviewAction,
  PullRequestLifecycleAction,
  Schema.Struct({
    kind: Schema.Literal("comment"),
    body: TrimmedNonEmptyString.check(Schema.isMaxLength(65000)),
  }),
  Schema.Struct({
    kind: Schema.Literal("edit-comment"),
    commentId: TrimmedNonEmptyString,
    commentVersion: TrimmedNonEmptyString,
    body: TrimmedNonEmptyString.check(Schema.isMaxLength(65000)),
  }),
  Schema.Struct({
    kind: Schema.Literal("reaction"),
    /** Null means the pull request itself rather than one of its comments. */
    commentId: Schema.NullOr(TrimmedNonEmptyString),
    content: PullRequestReactionContent,
    present: Schema.Boolean,
  }),
]);
export type PullRequestDiscussionAction = typeof PullRequestDiscussionAction.Type;
export const PullRequestDiscussionInput = Schema.Struct({
  ...PullRequestRef.fields,
  environmentId: EnvironmentId,
  commandId: CommandId,
  expectedRevision: TrimmedNonEmptyString,
  resourceVersion: TrimmedNonEmptyString,
  action: PullRequestDiscussionAction,
});
export type PullRequestDiscussionInput = typeof PullRequestDiscussionInput.Type;
